package dev.lingvo.ops.api

import dev.lingvo.shared.AuditPageDto
import dev.lingvo.shared.BillingDocumentDto
import dev.lingvo.shared.ops.OpsBillingDto
import dev.lingvo.shared.ops.OpsCancellationRowDto
import dev.lingvo.shared.ops.OpsCheckoutRowDto
import dev.lingvo.shared.ops.OpsCredentialRowDto
import dev.lingvo.shared.ops.OpsCronHealthDto
import dev.lingvo.shared.ops.OpsCreditEventRowDto
import dev.lingvo.shared.ops.OpsDeletionScopeDto
import dev.lingvo.shared.ops.OpsDeliveryRowDto
import dev.lingvo.shared.ops.OpsGitHubDto
import dev.lingvo.shared.ops.OpsGrantRowDto
import dev.lingvo.shared.ops.OpsIdentityDto
import dev.lingvo.shared.ops.OpsInvitationRowDto
import dev.lingvo.shared.ops.OpsJobDetailDto
import dev.lingvo.shared.ops.OpsJobHealthDto
import dev.lingvo.shared.ops.OpsMemberRowDto
import dev.lingvo.shared.ops.OpsPage
import dev.lingvo.shared.ops.OpsLogRowDto
import dev.lingvo.shared.ops.OpsReadDayRowDto
import dev.lingvo.shared.ops.OpsPaddleEventRowDto
import dev.lingvo.shared.ops.OpsProjectRowDto
import dev.lingvo.shared.ops.OpsProviderKeyHealthDto
import dev.lingvo.shared.ops.OpsProviderListDto
import dev.lingvo.shared.ops.OpsSearchDto
import dev.lingvo.shared.ops.OpsSeatHealthDto
import dev.lingvo.shared.ops.OpsSiteHealthDto
import dev.lingvo.shared.ops.OpsTokenMintRowDto
import dev.lingvo.shared.ops.OpsUserDto
import dev.lingvo.shared.ops.OpsUserRowDto
import dev.lingvo.shared.ops.OpsUserWorkspaceRowDto
import dev.lingvo.shared.ops.OpsWebhookRowDto
import dev.lingvo.shared.ops.OpsWindow
import dev.lingvo.shared.ops.OpsWorkspaceDto
import dev.lingvo.shared.ops.OpsWorkspaceRowDto
import dev.lingvo.site.api.API_JSON
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.genericFailure
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

/** A refusal from the console's API, with the status the page has to explain. */
class OpsApiException(val status: Int, override val message: String) : RuntimeException(message)

/** Every read the console makes, typed. This client has no request-body helper and cannot write. */
object OpsApi {
    private const val PREFIX = "/internal/ops"

    private suspend inline fun <reified T> read(path: String): T {
        val response = window.fetch(
            "${ApiConfig.baseUrl}$PREFIX$path",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    Session.token?.takeIf(String::isNotBlank)?.let { append("Authorization", "Bearer $it") }
                    append("Accept", "application/json")
                },
            ),
        ).await()
        if (!response.ok) {
            val status = response.status.toInt()
            throw OpsApiException(
                status = status,
                message = when (status) {
                    404 -> "Not found, or you are not on the operator allowlist. " +
                        "Check LINGVO_OPERATOR_USER_IDS before widening anything."
                    401 -> "This session is not signed in. Sign in again."
                    else -> genericFailure(status)
                },
            )
        }
        return API_JSON.decodeFromString(response.text().await())
    }

    suspend fun me(): OpsIdentityDto = read("/me")
    suspend fun search(query: String): OpsSearchDto = read("/search${query("q" to query)}")
    suspend fun workspaces(plan: String?, status: String?, query: String?, after: String?): OpsPage<OpsWorkspaceRowDto> =
        read("/workspaces${query("plan" to plan, "status" to status, "q" to query, "after" to after)}")
    suspend fun workspace(orgId: String): OpsWorkspaceDto = read("/workspaces/${segment(orgId)}")
    suspend fun members(orgId: String): OpsWindow<OpsMemberRowDto> = read("/workspaces/${segment(orgId)}/members")
    suspend fun providers(orgId: String): OpsProviderListDto = read("/workspaces/${segment(orgId)}/providers")
    suspend fun orgAudit(orgId: String, actor: String?, action: String?, after: String?): AuditPageDto = read(
        "/workspaces/${segment(orgId)}/audit${query("actor" to actor, "action" to action, "after" to after)}",
    )
    suspend fun projects(orgId: String): OpsWindow<OpsProjectRowDto> = read("/workspaces/${segment(orgId)}/projects")
    suspend fun invitations(orgId: String): OpsWindow<OpsInvitationRowDto> = read("/workspaces/${segment(orgId)}/invitations")
    suspend fun github(orgId: String): OpsGitHubDto = read("/workspaces/${segment(orgId)}/github")
    suspend fun deletionScope(orgId: String): OpsDeletionScopeDto = read("/workspaces/${segment(orgId)}/deletion-scope")
    suspend fun billing(orgId: String): OpsBillingDto = read("/workspaces/${segment(orgId)}/billing")
    suspend fun credits(orgId: String, after: String?): OpsPage<OpsCreditEventRowDto> = read(
        "/workspaces/${segment(orgId)}/credits${query("after" to after)}",
    )
    suspend fun grants(orgId: String): OpsWindow<OpsGrantRowDto> = read("/workspaces/${segment(orgId)}/grants")
    suspend fun checkouts(orgId: String): OpsWindow<OpsCheckoutRowDto> = read("/workspaces/${segment(orgId)}/checkouts")
    suspend fun cancellations(orgId: String): OpsWindow<OpsCancellationRowDto> = read("/workspaces/${segment(orgId)}/cancellations")
    suspend fun documents(orgId: String): OpsWindow<BillingDocumentDto> = read("/workspaces/${segment(orgId)}/documents")
    suspend fun paddleEvents(orgId: String, type: String?, after: String?): OpsPage<OpsPaddleEventRowDto> = read(
        "/workspaces/${segment(orgId)}/paddle-events${query("type" to type, "after" to after)}",
    )
    suspend fun jobs(orgId: String, after: String?): OpsPage<dev.lingvo.shared.ops.OpsMtJobRowDto> = read(
        "/workspaces/${segment(orgId)}/jobs${query("after" to after)}",
    )
    suspend fun job(orgId: String, jobId: String): OpsJobDetailDto = read(
        "/workspaces/${segment(orgId)}/jobs/${segment(jobId)}",
    )
    suspend fun webhooks(orgId: String): OpsWindow<OpsWebhookRowDto> = read("/workspaces/${segment(orgId)}/webhooks")
    suspend fun log(operator: String?, action: String?, scope: String, since: String?, until: String?, after: String?): OpsPage<OpsLogRowDto> =
        read("/log${query("operator" to operator, "action" to action, "scope" to scope, "since" to since, "until" to until, "after" to after)}")
    suspend fun readDays(): OpsWindow<OpsReadDayRowDto> = read("/log/reads")
    suspend fun deliveries(orgId: String, webhookId: String, after: String?): OpsPage<OpsDeliveryRowDto> = read(
        "/workspaces/${segment(orgId)}/webhooks/${segment(webhookId)}/deliveries${query("after" to after)}",
    )
    suspend fun users(query: String?, after: String?): OpsPage<OpsUserRowDto> = read("/users${query("q" to query, "after" to after)}")
    suspend fun user(userId: String): OpsUserDto = read("/users/${segment(userId)}")
    suspend fun userWorkspaces(userId: String): OpsWindow<OpsUserWorkspaceRowDto> = read("/users/${segment(userId)}/workspaces")
    suspend fun credentials(userId: String): OpsWindow<OpsCredentialRowDto> = read("/users/${segment(userId)}/credentials")
    suspend fun tokenMints(userId: String): OpsWindow<OpsTokenMintRowDto> = read("/users/${segment(userId)}/token-mints")
    suspend fun job(jobId: String): OpsJobDetailDto = read("/jobs/${segment(jobId)}")
    suspend fun webhook(webhookId: String): OpsWebhookRowDto = read("/webhooks/${segment(webhookId)}")
    suspend fun cronHealth(): OpsCronHealthDto = read("/health/cron")
    suspend fun checkoutHealth(): OpsWindow<OpsCheckoutRowDto> = read("/health/checkouts")
    suspend fun jobHealth(): OpsJobHealthDto = read("/health/jobs")
    suspend fun webhookHealth(): OpsWindow<OpsWebhookRowDto> = read("/health/webhooks")
    suspend fun deliveryHealth(): OpsWindow<OpsDeliveryRowDto> = read("/health/deliveries")
    suspend fun seatHealth(): OpsSeatHealthDto = read("/health/seats")
    suspend fun providerHealth(): OpsProviderKeyHealthDto = read("/health/providers")
    suspend fun siteHealth(): OpsSiteHealthDto = read("/health/site")
}

private fun segment(value: String): String = encodeURIComponent(value)

private fun query(vararg parameters: Pair<String, String?>): String {
    val present = parameters.filter { it.second != null }
    if (present.isEmpty()) return ""
    return present.joinToString(prefix = "?", separator = "&") { (name, value) ->
        "${encodeURIComponent(name)}=${encodeURIComponent(value.orEmpty())}"
    }
}

private external fun encodeURIComponent(value: String): String
